import { Badge, Button, Calendar, CalendarProps, Space, Tag } from "antd"
import Title from "antd/es/typography/Title"
import PageHeader from "modules/layout/dashboard/main/page-header"
import { useTranslation } from "react-i18next"
import { IExtEvent, useExtEvents, useExtSessions } from "../queries"
import styles from "../../styles.module.scss"
import Loader from "components/loader"
import { useEffect, useRef, useState } from "react"
import OverviewCardWithDate from "components/overview-card-with-date"
import { ReactComponent as InfoIcon } from "assets/icons/info.svg"
import { ReactComponent as PressignationIcon } from "assets/icons/person-add.svg"
import dayjs from "dayjs"
import { usePopup } from "utils/use-popup"
import EventDetailsModal from "pages/assignations/event-details-modal"
import CreatePreassignationDrawer from "./create-drawer"

const CreatePreassignation = () => {
  const { t } = useTranslation()
  const [calendarValue, setCalendarValue] =
    useState<CalendarProps<any>["value"]>(dayjs)
  const [calendarMode, setCalendarMode] =
    useState<CalendarProps<any>["mode"]>("month")

  const sessions = useExtSessions({
    filter: {
      status: { values: ["published", "progress"], operator: "in" },
    },
  })
  const events = useExtEvents(
    {
      filter: {
        session: {
          values: sessions.data?.map((session) => session.id),
          operator: "in",
        },
        status: { values: ["draft", "approved"], operator: "in" },
        visible_for_preassignation: { operator: "equals", values: [true] },
      },
      check_preassignation_for_employee: "user",
    },
    {
      enabled: !!sessions.data,
    }
  )

  const listRef = useRef(null)
  let shouldScroll = useRef(false)

  const eventDetailsModal = usePopup<any>()
  const createPreassignationDrawer = usePopup<any>()

  const CellRender: CalendarProps<any>["cellRender"] = (current, info) => {
    let filteredFvents: IExtEvent[] = []
    let backgroundColor = "transparent"
    if (info.type === "date") {
      filteredFvents =
        events.data?.filter(
          (event) => event.start_date === current.format("YYYY-MM-DD")
        ) ?? []
    }
    if (info.type === "month") {
      filteredFvents =
        events.data?.filter((event) =>
          event.start_date
            ? parseInt(event.start_date.split("-")[1]) === current.month() + 1
            : false
        ) ?? []
    }

    if (filteredFvents.length !== 0) {
      backgroundColor = "var(--color-primary)"
    }
    if (
      filteredFvents.some((el) => el.requested_employee_is_already_pre_assigned)
    ) {
      backgroundColor = "var(--color-neutral-200)"
    }

    return (
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: "5px",
        }}
      >
        <Badge
          showZero
          count={filteredFvents?.length}
          style={{
            backgroundColor,
          }}
        />
      </div>
    )
  }

  const handleSelect: CalendarProps<any>["onSelect"] = (date, selectInfo) => {
    shouldScroll.current = true
  }

  const handlePanelChange: CalendarProps<any>["onPanelChange"] = (
    date,
    mode
  ) => {
    setCalendarMode(mode)
  }

  useEffect(() => {
    if (shouldScroll.current && listRef.current) {
      ;(listRef.current as any)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      })
      shouldScroll.current = false
    }
  })

  return (
    <>
      <PageHeader
        title={
          <Title level={4}>
            {t("global.new", {
              entity: t("assignations.preassignation_one"),
              context: "female",
              count: 1,
            })}
          </Title>
        }
      />
      <div className={styles.calendarContainer}>
        {events.isLoading && (
          <div className={styles.calendarLoader}>
            <Loader />
          </div>
        )}
        <Calendar
          fullscreen={false}
          value={calendarValue}
          onChange={setCalendarValue}
          cellRender={CellRender}
          onPanelChange={handlePanelChange}
          mode={calendarMode}
          onSelect={handleSelect}
        />
      </div>
      <Space direction="vertical" ref={listRef}>
        {events.data
          ?.filter((el) =>
            calendarValue
              ? calendarMode === "month"
                ? el.start_date === calendarValue.format("YYYY-MM-DD")
                : parseInt(el.start_date?.split("-")[1]) ===
                  calendarValue.month() + 1
              : false
          )
          .sort(
            (a, b) =>
              new Date(a.start_date).getTime() -
              new Date(b.start_date).getTime()
          )
          .map(({ requested_employee_is_already_pre_assigned, ...event }) => (
            <OverviewCardWithDate
              key={event.id}
              date={event.start_date}
              actions={[
                <Tag
                  style={{
                    marginRight: 0,
                  }}
                  color={
                    requested_employee_is_already_pre_assigned
                      ? "var(--color-neutral-200)"
                      : "var(--color-primary)"
                  }
                >
                  {t(
                    `assignations.${
                      requested_employee_is_already_pre_assigned
                        ? "preassigned"
                        : "not_preassigned"
                    }`
                  )}
                </Tag>,
                <Button
                  style={{
                    width: "100%",
                  }}
                  icon={<InfoIcon height={20} />}
                  size="small"
                  type="text"
                  onClick={() => eventDetailsModal.open(event)}
                />,
                <Button
                  style={{
                    width: "100%",
                  }}
                  icon={<PressignationIcon height={20} />}
                  size="small"
                  type="text"
                  onClick={() => createPreassignationDrawer.open(event)}
                />,
              ]}
              Content={
                <>
                  <p>{event?.session?.name}</p>
                  <p>{event.name}</p>
                  <p>
                    {event.start_hour}
                    {" - "}
                    {event.end_hour}
                  </p>
                  <p>{event.place?.name ?? event.activity_room?.name}</p>
                </>
              }
            />
          ))}
      </Space>
      <EventDetailsModal
        {...eventDetailsModal}
        open={eventDetailsModal.isOpen}
      />
      <CreatePreassignationDrawer
        {...createPreassignationDrawer}
        open={createPreassignationDrawer.isOpen}
      />
    </>
  )
}

export default CreatePreassignation
